import React, { useCallback, useEffect, useState } from 'react';
import Select from 'react-select';
import { buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import { useWorkspace } from "./workspace/WorkspaceContext";

const timeFrameOptions = [
  { label: '5 min', value: '5' },
  { label: '15 min', value: '15' },
  { label: '30 min', value: '30' },
  { label: '1 hour', value: '60' },
  { label: '4 hour', value: '240' },
  { label: '8 hour', value: '480' },
  { label: '12 hour', value: '720' },
  { label: '1 day', value: '1440' },
];

const GlobalFilterBar = ({
  selectedContext,
  onContextChange,
  selectedTimeFrames,
  onTimeFramesChange,
  selectedSymbols,
  onSymbolsChange,
}) => {
  const { workspace } = useWorkspace();
  const [coinDropdownOptions, setCoinDropdownOptions] = useState([]);

  // Fetch symbol list from API
  const fetchCoinDropdown = useCallback(async () => {
    try {
      const response = await fetch(
        buildScopedApiUrl('/api/bot/ui/get/getRecentActiveAlertSymbols', workspace),
        {
          headers: getApiHeaders(),
        }
      );
      const coins = await response.json();
      const normalizedCoins = ensureArray(coins);
      if (!Array.isArray(coins)) {
        console.warn('Unexpected symbol helper payload shape', workspace, coins);
      }
      setCoinDropdownOptions(normalizedCoins);
    } catch (err) {
      console.error('Failed to fetch coin dropdown list:', err);
      setCoinDropdownOptions([]);
    }
  }, [workspace]);

  useEffect(() => {
    fetchCoinDropdown();
  }, [fetchCoinDropdown]);

  const clearAllFilters = () => {
    onSymbolsChange([]);
    onTimeFramesChange([]);
  };

  // shared Signal Fly light style for react-select
  const customSelectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: '#eafdff',
      borderColor: '#b9e4ea',
      minHeight: 38,
      boxShadow: 'none',
      ':hover': { borderColor: '#7fd4de' },
    }),
    input: (base) => ({ ...base, color: '#073344' }),
    placeholder: (base) => ({ ...base, color: '#277986' }),
    menu: (base) => ({
      ...base,
      backgroundColor: '#ffffff',
      color: '#073344',
      border: '1px solid #b9e4ea',
      boxShadow: '0 12px 26px rgba(8, 47, 73, 0.12)',
      zIndex: 999,
      maxHeight: 250,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#0f7180' : '#ffffff',
      color: state.isSelected ? '#ffffff' : '#073344',
      cursor: 'pointer',
      ':hover': {
        backgroundColor: '#dff7f5',
        color: '#073344',
      },
      ':active': {
        backgroundColor: '#ccecef',
      },
    }),
    menuList: (base) => ({
      ...base,
      paddingTop: 4,
      paddingBottom: 4,
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#dff7f5',
      border: '1px solid #b9e4ea',
    }),
    multiValueLabel: (base) => ({ ...base, color: '#073344', fontWeight: 700 }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#277986',
      ':hover': { backgroundColor: '#b9e4ea', color: '#073344' },
    }),
    singleValue: (base) => ({ ...base, color: '#073344' }),
    dropdownIndicator: (base) => ({ ...base, color: '#277986' }),
    indicatorSeparator: (base) => ({ ...base, backgroundColor: '#b9e4ea' }),
  };

  return (
    <div className="filter-primary-row">
      <div className="filter-field filter-field-context">
        <label>Workspace:</label>
        <div className="filter-context-select">
          {workspace.exchange} / {workspace.marketType}
        </div>
      </div>

      {/* ---------------- CONTEXT SELECTOR ---------------- */}
      <div className="filter-field filter-field-context">
        <label>Context:</label>
        <select
          className="filter-context-select"
          value={selectedContext}
          onChange={(e) => onContextChange(e.target.value)}
        >
          <option value="gainers">Top Gainers</option>
          <option value="losers">Top Losers</option>
          <option value="volume">Top Volume</option>
          <option value="trend">Trend dashboard</option>
          <option value="cross">Cross Signals</option>
          <option value="tp">Additional TP Signals</option>
          <option value="adx">Top ADX Trend</option>
        </select>
      </div>

      {/* ---------------- SYMBOL MULTI-SELECT ---------------- */}
      <div className="filter-field filter-field-symbols">
        <label>Symbols:</label>
        <Select
          isMulti
          classNamePrefix="sf-filter-select"
          placeholder="Select Symbols..."
          options={ensureArray(coinDropdownOptions).map((c) => ({ label: c, value: c }))}
          value={ensureArray(selectedSymbols).map((s) => ({ label: s, value: s }))}
          onChange={(selected) => onSymbolsChange(ensureArray(selected).map((x) => x.value))}
          styles={customSelectStyles}
          noOptionsMessage={() => 'No symbols found'}
        />
      </div>

      {/* ---------------- TIMEFRAME MULTI-SELECT ---------------- */}
      <div className="filter-field filter-field-timeframes">
        <label>Time Frames:</label>
        <Select
          isMulti
          classNamePrefix="sf-filter-select"
          placeholder="Select Time Frames..."
          options={timeFrameOptions}
          value={ensureArray(selectedTimeFrames)
            .map((v) => {
              const tf = timeFrameOptions.find((t) => t.value === v);
              return tf ? { label: tf.label, value: tf.value } : null;
            })
            .filter(Boolean)}
          onChange={(selected) => onTimeFramesChange(ensureArray(selected).map((x) => x.value))}
          styles={customSelectStyles}
          noOptionsMessage={() => 'No time frames'}
        />
      </div>

      {/* ---------------- CLEAR ALL FILTERS ---------------- */}
      <button className="filter-clear-btn" onClick={clearAllFilters}>
        Clear All Filters
      </button>
    </div>
  );
};

export default GlobalFilterBar;

